// Analysis tab (handoff Sections 8.1 and 9.2). Everything here is anonymised
// aggregate: item and section means with their distributions, strengths,
// priorities, divergences, safeguarding items surfaced individually, and
// participation. No composite church score exists, deliberately: these are
// prompts for a pastoral conversation, not a league table.

import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import {
  exportUrls,
  healthApi,
  HealthApiError,
  type CycleAiSummary,
  type CycleDetail,
  type ItemStat,
  type QualAnalysis,
  type QuantAnalysis,
  type SectionThemes,
  type ThemeSignal,
  type VerbatimSection,
} from './api';
import { BAND_LABELS, distributionEvidence, formatNzDate, roleLabel } from './format';
import { renderInstrumentText } from './markup';
import { SectionRadar } from './charts/Radar';
import { DistributionBars } from './charts/DistributionBars';

// Fallback when an older worker payload lacks min_n_text; the live threshold
// comes from the cycle participation response.
const MIN_N_TEXT_DEFAULT = 4;

// How long the tab keeps checking for summaries while a pass runs (~4.5 min;
// a full pass is usually one to two). After that the banner stops promising.
const QUAL_POLL_MS = 7000;
const QUAL_POLL_MAX = 40;

export function AnalysisTab({ detail }: { detail: CycleDetail }): JSX.Element {
  const { cycle, participation } = detail;
  const minNText = participation.min_n_text ?? MIN_N_TEXT_DEFAULT;
  const canManage = detail.can_manage;
  const [quant, setQuant] = useState<QuantAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qual, setQual] = useState<QualAnalysis | null>(null);
  // undefined = no refresh in flight; otherwise the generated_at we are
  // waiting to see change (null = there was no cache before).
  const [regenFrom, setRegenFrom] = useState<string | null | undefined>(undefined);
  const [regenBusy, setRegenBusy] = useState(false);
  const [qualNotice, setQualNotice] = useState<string | null>(null);
  const [reportNotice, setReportNotice] = useState<string | null>(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const pollCount = useRef(0);

  useEffect(() => {
    healthApi
      .quant(cycle.id)
      .then(setQuant)
      .catch(() =>
        setError('The analysis could not be loaded just now. Please try again shortly.'),
      );
  }, [cycle.id]);

  const loadQual = useCallback(() => {
    // Qualitative is additive: a fetch failure never blocks the numbers, and
    // never wipes summaries that already loaded.
    healthApi
      .qual(cycle.id)
      .then(setQual)
      .catch(() => {});
  }, [cycle.id]);

  useEffect(() => {
    pollCount.current = 0;
    setPollExhausted(false);
    loadQual();
  }, [loadQual]);

  const waitingForRefresh = regenFrom !== undefined && (qual?.generated_at ?? null) === regenFrom;
  const polling = qual?.status === 'pending' || waitingForRefresh;
  useEffect(() => {
    if (!polling) return;
    if (pollCount.current >= QUAL_POLL_MAX) {
      setPollExhausted(true);
      setRegenFrom(undefined);
      return;
    }
    const t = window.setTimeout(() => {
      pollCount.current += 1;
      loadQual();
    }, QUAL_POLL_MS);
    return () => window.clearTimeout(t);
  }, [polling, qual, loadQual]);
  useEffect(() => {
    if (regenFrom !== undefined && (qual?.generated_at ?? null) !== regenFrom) {
      setRegenFrom(undefined);
      setQualNotice(null);
    }
  }, [qual, regenFrom]);

  const regenerate = (): void => {
    if (regenBusy) return;
    setRegenBusy(true);
    setQualNotice(null);
    healthApi
      .regenerateQual(cycle.id)
      .then(() => {
        pollCount.current = 0;
        setPollExhausted(false);
        setRegenFrom(qual?.generated_at ?? null);
      })
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        setQualNotice(
          code === 'rate_limited'
            ? 'The summaries were refreshed recently. Please try again in a little while.'
            : code === 'cycle_open'
              ? 'Summaries are prepared once the assessment has closed.'
              : 'The summaries could not be refreshed just now. Please try again shortly.',
        );
      })
      .finally(() => setRegenBusy(false));
  };

  const downloadPdf = (): void => {
    // Fetched rather than a bare link so a rendering hiccup shows a message
    // instead of saving a JSON error body as a .pdf (same reasoning as the
    // gated CSV links below).
    setQualNotice(null);
    fetch(exportUrls.summaryPdf(cycle.id), { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        const name =
          res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ??
          'health-debrief.pdf';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() =>
        setQualNotice('The debrief pack could not be prepared just now. Please try again shortly.'),
      );
  };

  const downloadReport = (): void => {
    // Fetched (not a bare link) for the same reason as the debrief pack: a
    // render hiccup or a gate response shows a message rather than saving a
    // JSON error body as a .pdf.
    setReportNotice(null);
    setReportBusy(true);
    fetch(exportUrls.reportPdf(cycle.id), { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        const name =
          res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ??
          'health-report.pdf';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() =>
        setReportNotice('The report could not be prepared just now. Please try again shortly.'),
      )
      .finally(() => setReportBusy(false));
  };

  if (error) {
    return (
      <div className="card">
        <p style={{ fontSize: 14, color: 'var(--health-grey)' }}>{error}</p>
      </div>
    );
  }
  if (!quant) return <p style={{ color: 'var(--health-grey)' }}>Loading the analysis...</p>;

  if (quant.suppressed) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
        <h3 style={{ fontSize: 17, marginBottom: 8 }}>
          Not enough responses yet to report this anonymously
        </h3>
        <p
          style={{
            fontSize: 14,
            color: 'var(--health-grey)',
            lineHeight: 1.7,
            maxWidth: 480,
            margin: '0 auto',
          }}
        >
          Results appear once at least {participation.min_n} people have submitted, so that no
          one&apos;s individual answers can be picked out. {quant.submitted} of {quant.invited}{' '}
          invited have submitted so far.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {quant.smallSample && (
        <div className="health-banner" role="note">
          A gentle caution: with a small group, numbers are indicative and easily attributable.
          Treat what follows as a prompt for conversation rather than a measurement.
        </div>
      )}

      {/* Whole-cycle summary of the written answers */}
      <QualSummarySlot
        qual={qual}
        canManage={canManage}
        minNText={minNText}
        refreshing={waitingForRefresh}
        pollExhausted={pollExhausted}
        regenBusy={regenBusy}
        notice={qualNotice}
        onRegenerate={regenerate}
        onDownloadPdf={downloadPdf}
      />

      {/* Overview — the radar read alongside a numbered score table, given the
          full width so both have room to breathe. */}
      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 2 }}>Overview</h3>
        <p
          style={{
            fontSize: 12.5,
            color: 'var(--health-grey)',
            marginBottom: 14,
            lineHeight: 1.6,
          }}
        >
          Each spoke is one of the fourteen areas, on the 1 to 4 scale — the number on each spoke
          matches the table beside it. The shape is to be read together, not ranked: sections carry
          different numbers of items.
        </p>
        <div className="health-overview">
          <div className="health-overview-chart">
            <SectionRadar sections={quant.sections} />
          </div>
          <table className="table health-overview-table">
            <thead>
              <tr>
                <th scope="col" style={{ width: 34, textAlign: 'center' }}>
                  #
                </th>
                <th scope="col">Area</th>
                <th scope="col" style={{ textAlign: 'center', width: 64 }}>
                  Score
                </th>
                <th scope="col" style={{ width: 130 }}>
                  Band
                </th>
              </tr>
            </thead>
            <tbody>
              {quant.sections.map((s) => (
                <tr key={s.section_no}>
                  <td style={{ textAlign: 'center' }}>
                    <NumBadge n={s.section_no} />
                  </td>
                  <td>{renderInstrumentText(s.title)}</td>
                  <td
                    style={{
                      textAlign: 'center',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {s.mean !== null ? (
                      s.mean.toFixed(1)
                    ) : (
                      <span style={{ color: 'var(--health-grey)' }}>—</span>
                    )}
                  </td>
                  <td>{s.band !== null && <BandChip band={s.band} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Participation + exports */}
      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 10 }}>Participation</h3>
        <p style={{ fontSize: 14, marginBottom: 6 }}>
          <strong>{quant.submitted}</strong> of <strong>{quant.invited}</strong> invited have
          submitted{quant.completionPct !== null ? ` (${quant.completionPct}%)` : ''}.
        </p>
        {quant.roleParticipation && Object.keys(quant.roleParticipation).length > 0 ? (
          <table className="table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th scope="col">Role</th>
                <th scope="col">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(quant.roleParticipation).map(([role, count]) => (
                <tr key={role}>
                  <td>{roleLabel(role)}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ fontSize: 12.5, color: 'var(--health-grey)', lineHeight: 1.6 }}>
            A role-by-role view is shown only when each group is large enough to stay anonymous.
          </p>
        )}
        <div className="btn-set" style={{ marginTop: 16 }}>
          {/* The full report is the headline export: the whole analysis as a
                print-paginated PDF. Fetched, so a gate/render error surfaces as
                a message. Available whenever the analysis is (not suppressed). */}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={downloadReport}
            disabled={reportBusy}
          >
            {reportBusy ? 'Preparing report...' : 'Download full report (PDF)'}
          </button>
          {/* Both CSV exports are gated so a link never downloads a JSON error
                body as a .csv. Scores unlock with the analysis itself; written
                answers have their own higher threshold (min_n_text, from the
                participation payload). */}
          {!quant.suppressed ? (
            <a className="btn btn-ghost btn-sm" href={exportUrls.scores(cycle.id)} download>
              Download responses (CSV)
            </a>
          ) : (
            <DisabledExport title="Responses can be downloaded once enough people have submitted for the results to be anonymous.">
              Download responses (CSV)
            </DisabledExport>
          )}
          {!quant.suppressed && quant.submitted >= minNText && cycle.status !== 'open' ? (
            <a className="btn btn-ghost btn-sm" href={exportUrls.qualitative(cycle.id)} download>
              Download written answers (CSV)
            </a>
          ) : (
            <DisabledExport
              title={
                cycle.status === 'open'
                  ? 'Written answers can be downloaded once the assessment has closed, so answers can never be matched to who submitted when.'
                  : `Written answers can be downloaded once at least ${minNText} people have submitted, so no one's words can be picked out.`
              }
            >
              Download written answers (CSV)
            </DisabledExport>
          )}
        </div>
        {reportNotice && (
          <p style={{ fontSize: 12.5, color: 'var(--band-priority)', marginTop: 8 }}>
            {reportNotice}
          </p>
        )}
      </div>

      {/* Safeguarding items, never buried — and never silently absent. */}
      {quant.criticalItems.length > 0 ? (
        <div className="card" style={{ borderLeft: '4px solid var(--band-priority)' }}>
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Safeguarding essentials</h3>
          <p
            style={{
              fontSize: 12.5,
              color: 'var(--health-grey)',
              marginBottom: 12,
              lineHeight: 1.6,
            }}
          >
            These items concern the safety of children and vulnerable people, so they are always
            shown on their own and never folded into a section average. A flag means at least one
            responder answered 1 or 2, or the average sits below 3.
          </p>
          <ItemList items={quant.criticalItems} showFlag />
        </div>
      ) : (
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Safeguarding essentials</h3>
          <p style={{ fontSize: 12.5, color: 'var(--health-grey)', lineHeight: 1.6 }}>
            No questions in this assessment's instrument are designated as safeguarding, so there is
            nothing to surface here. Designations are set in the instrument editor (or, for older
            versions, in the module settings).
          </p>
        </div>
      )}

      {/* The three lists */}
      <div
        className="card-row"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 16,
        }}
      >
        <ListCard
          title="Where things are embedded"
          intro="The areas responders most consistently see as healthy and established."
          empty="No items sit in the Embedded band yet."
          items={quant.strengths}
        />
        <ListCard
          title="Where attention is invited"
          intro="The items with the most room to grow. An invitation to prayer and planning, not a verdict."
          empty="Nothing stands out for attention."
          items={quant.priorities}
        />
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Where responders see it differently</h3>
          <p
            style={{
              fontSize: 12.5,
              color: 'var(--health-grey)',
              marginBottom: 12,
              lineHeight: 1.6,
            }}
          >
            Items where answers genuinely diverge. Who sees what differently is often the most
            useful thing to talk about together.
          </p>
          {quant.divergences.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>
              No strong divergence between responders on any item.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
              {quant.divergences.map((item) => (
                <li
                  key={item.item_id}
                  style={{ borderBottom: '1px solid #f0ebde', paddingBottom: 10 }}
                >
                  <p style={{ fontSize: 13.5, lineHeight: 1.5, marginBottom: 3 }}>
                    {renderInstrumentText(item.text)}
                  </p>
                  <p style={{ fontSize: 12.5, color: 'var(--health-grey)' }}>
                    {distributionEvidence(item.distribution)}
                    {item.roleMeans && (
                      <span>
                        {' '}
                        (
                        {Object.entries(item.roleMeans)
                          .sort((a, b) => b[1].mean - a[1].mean)
                          .map(([role, rm]) => `${roleLabel(role)} ${rm.mean}`)
                          .join(', ')}
                        )
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Governance items, always visible, not alarm styled */}
      {quant.governanceItems.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Governance and compliance</h3>
          <p
            style={{
              fontSize: 12.5,
              color: 'var(--health-grey)',
              marginBottom: 12,
              lineHeight: 1.6,
            }}
          >
            Shown individually so they stay visible within their section.
          </p>
          <ItemList items={quant.governanceItems} />
        </div>
      )}

      {/* Per-section detail */}
      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>Section by section</h3>
        <div style={{ display: 'grid', gap: 20 }}>
          {quant.sections.map((section) => {
            const items = quant.items.filter((i) => i.section_no === section.section_no);
            const themes =
              qual?.status === 'ready'
                ? (qual.themes ?? []).find((t) => t.section_no === section.section_no)
                : undefined;
            const verbatim = (qual?.verbatim ?? []).find(
              (v) => v.section_no === section.section_no,
            );
            return (
              <div key={section.section_no}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginBottom: 8,
                  }}
                >
                  <h4 style={{ fontSize: 14 }}>
                    {section.section_no}. {renderInstrumentText(section.title)}
                  </h4>
                  {section.mean !== null && section.band !== null && (
                    <>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{section.mean}</span>
                      <BandChip band={section.band} />
                    </>
                  )}
                </div>
                {items.length === 0 ? (
                  <p style={{ fontSize: 12.5, color: 'var(--health-grey)' }}>
                    No answers in this section yet.
                  </p>
                ) : (
                  <ItemList items={items} showFlag />
                )}
                {themes && <SectionThemesPanel themes={themes} />}
                {verbatim && <VerbatimPanel verbatim={verbatim} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Qualitative pieces ────────────────────────────────────────────────────────

const SIGNAL_PILLS: Record<ThemeSignal, { className: string; label: string }> = {
  strength: { className: 'pill pill-success', label: 'Strength' },
  watch: { className: 'pill pill-warn', label: 'Watch' },
  risk: { className: 'pill pill-danger', label: 'Attention' },
  mixed: { className: 'pill pill-info', label: 'Mixed views' },
};

/** The slot above the overview: the whole-cycle summary when it exists, or a
 *  quiet explanation of why it does not yet. The wording deliberately says
 *  "summarised", matching the promise made to respondents. */
function QualSummarySlot({
  qual,
  canManage,
  minNText,
  refreshing,
  pollExhausted,
  regenBusy,
  notice,
  onRegenerate,
  onDownloadPdf,
}: {
  qual: QualAnalysis | null;
  canManage: boolean;
  minNText: number;
  refreshing: boolean;
  pollExhausted: boolean;
  regenBusy: boolean;
  notice: string | null;
  onRegenerate: () => void;
  onDownloadPdf: () => void;
}): JSX.Element | null {
  if (!qual) return null;

  if ((qual.status === 'pending' || refreshing) && !pollExhausted) {
    return (
      <div className="health-banner health-banner-blue" role="status">
        The written answers are being summarised. This usually takes a minute or two; the page will
        update itself.
      </div>
    );
  }
  if ((qual.status === 'pending' || refreshing) && pollExhausted) {
    return (
      <div className="health-banner" role="status">
        The summaries are taking longer than expected. They will appear here; please check back
        shortly.
      </div>
    );
  }
  if (qual.status === 'suppressed') {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--health-grey)', lineHeight: 1.6 }}>
        Written answers are reported once at least {minNText} people have submitted, so no
        one&apos;s words can be picked out.
      </p>
    );
  }
  if (qual.status === 'awaiting_close') {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--health-grey)', lineHeight: 1.6 }}>
        Written answers are kept aside while the assessment is open, and are summarised and shown
        here once it closes.
      </p>
    );
  }
  if (qual.status === 'unavailable') {
    return (
      <div className="card" style={{ padding: '16px 20px' }}>
        <p style={{ fontSize: 12.5, color: 'var(--health-grey)', lineHeight: 1.6 }}>
          The written answers are shown as given in each section below. Summaries are not available
          yet.
        </p>
        {canManage && (
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={regenBusy}
              onClick={onRegenerate}
            >
              {regenBusy ? 'Starting...' : 'Prepare the summaries'}
            </button>
            {notice && (
              <span style={{ fontSize: 12.5, color: 'var(--health-grey)' }}>{notice}</span>
            )}
          </div>
        )}
      </div>
    );
  }
  if (qual.status !== 'ready') return null;

  if (!qual.summary) {
    // Section themes landed but the whole-picture summary did not (its model
    // call failed or was cut short). Not a dead end: managers can re-run it.
    return (
      <div className="card" style={{ padding: '16px 20px' }}>
        <p style={{ fontSize: 12.5, color: 'var(--health-grey)', lineHeight: 1.6 }}>
          The section summaries are ready below. The whole-picture summary is still to come.
        </p>
        {canManage && (
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={regenBusy}
              onClick={onRegenerate}
            >
              {regenBusy ? 'Starting...' : 'Prepare the whole-picture summary'}
            </button>
            {notice && (
              <span style={{ fontSize: 12.5, color: 'var(--health-grey)' }}>{notice}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <SummaryCard
      summary={qual.summary}
      qual={qual}
      canManage={canManage}
      regenBusy={regenBusy}
      notice={notice}
      onRegenerate={onRegenerate}
      onDownloadPdf={onDownloadPdf}
    />
  );
}

function SummaryCard({
  summary,
  qual,
  canManage,
  regenBusy,
  notice,
  onRegenerate,
  onDownloadPdf,
}: {
  summary: CycleAiSummary;
  qual: QualAnalysis;
  canManage: boolean;
  regenBusy: boolean;
  notice: string | null;
  onRegenerate: () => void;
  onDownloadPdf: () => void;
}): JSX.Element {
  return (
    <div className="card" style={{ borderLeft: '4px solid var(--health-accent)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 4,
        }}
      >
        <h3 style={{ fontSize: 15 }}>Reading the whole picture</h3>
        {qual.generated_at && (
          <span style={{ fontSize: 11.5, color: 'var(--health-grey)' }}>
            summarised {formatNzDate(qual.generated_at)}
          </span>
        )}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginBottom: 12, lineHeight: 1.6 }}>
        A summary of the written answers and the numbers together, prepared for the debrief
        conversation. Treat it as a starting point, not a verdict.
      </p>
      {summary.narrative.map((p, i) => (
        <p key={i} style={{ fontSize: 13.5, lineHeight: 1.7, marginBottom: 10 }}>
          {p}
        </p>
      ))}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 16,
          marginTop: 6,
        }}
      >
        {summary.give_thanks.length > 0 && (
          <div>
            <h4 style={{ fontSize: 13, marginBottom: 6 }}>Give thanks for</h4>
            <SummaryList items={summary.give_thanks} />
          </div>
        )}
        {summary.attend_to.length > 0 && (
          <div>
            <h4 style={{ fontSize: 13, marginBottom: 6 }}>Attend to</h4>
            <SummaryList items={summary.attend_to} />
          </div>
        )}
      </div>
      {summary.divergences.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4 style={{ fontSize: 13, marginBottom: 6 }}>Where leaders see it differently</h4>
          <SummaryList items={summary.divergences} />
        </div>
      )}
      {summary.debrief_questions.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4 style={{ fontSize: 13, marginBottom: 6 }}>Questions for the debrief</h4>
          <ol
            style={{
              margin: 0,
              paddingLeft: 20,
              fontSize: 13,
              lineHeight: 1.7,
              display: 'grid',
              gap: 4,
            }}
          >
            {summary.debrief_questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ol>
        </div>
      )}
      <div
        style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <button type="button" className="btn btn-primary btn-sm" onClick={onDownloadPdf}>
          Download the debrief pack (PDF)
        </button>
        {canManage && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={regenBusy}
            onClick={onRegenerate}
            title="Prepare the summaries again from the current answers"
          >
            {regenBusy ? 'Starting...' : 'Refresh the summaries'}
          </button>
        )}
        {notice && <span style={{ fontSize: 12.5, color: 'var(--health-grey)' }}>{notice}</span>}
      </div>
    </div>
  );
}

function SummaryList({ items }: { items: string[] }): JSX.Element {
  return (
    <ul
      style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7, display: 'grid', gap: 4 }}
    >
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function SectionThemesPanel({ themes }: { themes: SectionThemes }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 10,
        background: 'var(--health-accent-lt)',
        border: '1px solid #d8e3c2',
        borderRadius: 'var(--health-radius-sm)',
        padding: '12px 16px',
      }}
    >
      <h5
        style={{
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 1,
          color: 'var(--health-accent-dk)',
          marginBottom: 8,
        }}
      >
        From the written answers
      </h5>
      <div style={{ display: 'grid', gap: 10 }}>
        {themes.themes.map((t, i) => (
          <div key={i}>
            <p style={{ fontSize: 13, marginBottom: 2 }}>
              <strong>{t.title}</strong>{' '}
              <span className={SIGNAL_PILLS[t.signal].className} style={{ marginLeft: 4 }}>
                {SIGNAL_PILLS[t.signal].label}
              </span>
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.6 }}>{t.summary}</p>
            {t.points.length > 0 && (
              <ul
                style={{
                  margin: '4px 0 0',
                  paddingLeft: 18,
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: 'var(--health-grey)',
                }}
              >
                {t.points.map((p, j) => (
                  <li key={j}>{p}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      {themes.questions_for_debrief.length > 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginTop: 10, lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--health-ink)' }}>Worth asking:</strong>{' '}
          {themes.questions_for_debrief.join(' ')}
        </p>
      )}
    </div>
  );
}

/** Verbatim answers, below the fold. Already hash-shuffled and identity-free
 *  server-side; the server also withholds them below MIN_N_TEXT. */
function VerbatimPanel({ verbatim }: { verbatim: VerbatimSection }): JSX.Element {
  const total = verbatim.questions.reduce((n, q) => n + q.answers.length, 0);
  return (
    <details style={{ marginTop: 8 }}>
      <summary style={{ fontSize: 12.5, color: 'var(--health-grey)', cursor: 'pointer' }}>
        What people wrote ({total} {total === 1 ? 'answer' : 'answers'})
      </summary>
      <div style={{ display: 'grid', gap: 10, padding: '10px 0 4px 14px' }}>
        {verbatim.questions.map((q) => (
          <div key={q.id}>
            <p style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
              {renderInstrumentText(q.text)}
            </p>
            <ul style={{ margin: 0, paddingLeft: 16, display: 'grid', gap: 4 }}>
              {q.answers.map((a, i) => (
                <li
                  key={i}
                  style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--health-grey)' }}
                >
                  {a}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
}

// ── Shared pieces ─────────────────────────────────────────────────────────────

function DisabledExport({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <span
      className="btn btn-ghost btn-sm"
      aria-disabled="true"
      title={title}
      style={{ opacity: 0.55, cursor: 'not-allowed' }}
    >
      {children}
    </span>
  );
}

function BandChip({ band }: { band: ItemStat['band'] }): JSX.Element {
  return <span className={`chip chip-${band}`}>{BAND_LABELS[band]}</span>;
}

/** The section number as a small green badge, so the number on each radar spoke
 *  reads as the same key as the row in the score table. */
function NumBadge({ n }: { n: number }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: 'var(--health-accent-lt)',
        color: 'var(--health-accent-dk)',
        fontSize: 12,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {n}
    </span>
  );
}

function ItemList({ items, showFlag }: { items: ItemStat[]; showFlag?: boolean }): JSX.Element {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
      {items.map((item) => (
        <li
          key={item.item_id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '1px solid #f0ebde',
            paddingBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 260px', minWidth: 220 }}>
            <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>
              {item.group ? (
                <span style={{ color: 'var(--health-grey)' }}>
                  {renderInstrumentText(item.group)}:{' '}
                </span>
              ) : null}
              {renderInstrumentText(item.text)}
            </p>
            <p style={{ fontSize: 12, color: 'var(--health-grey)', marginTop: 2 }}>
              {item.mean} from {item.count} of {item.respondents}
            </p>
          </div>
          <DistributionBars distribution={item.distribution} />
          <BandChip band={item.band} />
          {item.divergent && <span className="pill pill-warn">Views differ</span>}
          {showFlag && item.criticalFlagged && (
            <span className="pill pill-danger">Needs attention</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function ListCard({
  title,
  intro,
  empty,
  items,
}: {
  title: string;
  intro: string;
  empty: string;
  items: ItemStat[];
}): JSX.Element {
  return (
    <div className="card">
      <h3 style={{ fontSize: 15, marginBottom: 4 }}>{title}</h3>
      <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginBottom: 12, lineHeight: 1.6 }}>
        {intro}
      </p>
      {items.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>{empty}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
          {items.map((item) => (
            <li key={item.item_id} style={{ borderBottom: '1px solid #f0ebde', paddingBottom: 10 }}>
              <p style={{ fontSize: 13.5, lineHeight: 1.5, marginBottom: 3 }}>
                {renderInstrumentText(item.text)}
              </p>
              <p
                style={{
                  fontSize: 12.5,
                  color: 'var(--health-grey)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span>
                  {item.mean} from {item.count} of {item.respondents}
                </span>
                <BandChip band={item.band} />
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
